import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useReducer, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {RESULTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {Schema} from 'schemas/SchemaConstants';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_POWDER} from 'ui-library/Colors';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import * as ImportUtil from 'util/ImportUtil';
import {assertNever} from 'util/Types';

import {
  LabAutomationTransform,
  AutomationInputFileConfigSpec,
  ClientAutomationOutputFileConfigSpec,
  TransformPreviewFile,
} from '../RunSchemaSpecConstants';
import {generateTransformPreviews, generateBaseFilePreview} from './LabAutomationTransformsDAO';
import {ReorderableTransformsEditor} from './ReorderableTransformsEditor';
import {isTransformValid} from './transform-form/TransformFormUtils';
import TransformPreview from './TransformPreview';
import UploadPreviewBaseFileButton from './UploadPreviewBaseFileButton';

export enum TransformPreviewActionType {
  GENERATE_PREVIEW_STARTED = 'GENERATE_PREVIEW_STARTED',
  GENERATE_PREVIEW_SUCCEEDED = 'GENERATE_PREVIEW_SUCCEEDED',
  GENERATE_PREVIEW_FAILED = 'GENERATE_PREVIEW_FAILED',
  // oxlint-disable-next-line no-duplicate-enum-values
  SET_BASE_FILE_STARTED = 'SET_BASE_FILE_STARTED',
  SET_BASE_FILE_SUCCEEDED = 'SET_BASE_FILE_STARTED',
}

export type TransformPreviewAction =
  | {type: TransformPreviewActionType.GENERATE_PREVIEW_STARTED; requestId: string}
  | {
      type: TransformPreviewActionType.GENERATE_PREVIEW_SUCCEEDED;
      requestId: string;
      previewFile: TransformPreviewFile;
    }
  | {type: TransformPreviewActionType.GENERATE_PREVIEW_FAILED}
  | {type: TransformPreviewActionType.SET_BASE_FILE_STARTED}
  | {type: TransformPreviewActionType.SET_BASE_FILE_SUCCEEDED};

interface TransformPreviewState {
  requestId: string | null;
  isLoadingPreviews: boolean;
  previewFile: TransformPreviewFile | null;
}

interface BaseFileState {
  name: string;
  unsignedUrl: string;
  signedUrl: string;
}

export function transformPreviewReducer(
  state: TransformPreviewState,
  action: TransformPreviewAction
): TransformPreviewState {
  switch (action.type) {
    case TransformPreviewActionType.GENERATE_PREVIEW_STARTED:
      return {...state, isLoadingPreviews: true, requestId: action.requestId};
    case TransformPreviewActionType.GENERATE_PREVIEW_SUCCEEDED:
      if (action.requestId !== state.requestId) {
        return state;
      }
      return {...state, isLoadingPreviews: false, previewFile: action.previewFile};
    case TransformPreviewActionType.GENERATE_PREVIEW_FAILED:
      return {...state, isLoadingPreviews: false};
    case TransformPreviewActionType.SET_BASE_FILE_STARTED:
      return {...state, isLoadingPreviews: true};
    case TransformPreviewActionType.SET_BASE_FILE_SUCCEEDED:
      return {...state, previewFile: null};
    default:
      throw new Error(`Invalid action type ${assertNever(action)}`);
  }
}

export interface LabAutomationTransformsModalProps {
  allowPreview?: boolean;
  analyticsMetadata?: object;
  automationFileConfig: ClientAutomationOutputFileConfigSpec | AutomationInputFileConfigSpec;
  baseSchema: Schema;
  initialTransforms: Array<LabAutomationTransform> | undefined;
  onRequestClose: () => void;
  onRequestSubmit: (transforms: Array<LabAutomationTransform>) => void;
}

export default function LabAutomationTransformsModal(props: LabAutomationTransformsModalProps): JSX.Element {
  const [transformPreviewState, dispatchTransformPreviewState] = useReducer(transformPreviewReducer, {
    requestId: null,
    isLoadingPreviews: false,
    previewFile: null,
  });
  const [transforms, setTransforms] = useState(props.initialTransforms || [{transformType: null}]);
  const [submitAttempts, setSubmitAttempts] = useState(0); // Counter so child component can react to errors
  const [baseFileState, setBaseFileState] = useState<BaseFileState | null>(null);

  const previewFeatureEnabled = BenchlingConfig.LAB_AUTOMATION_ENABLE_TRANSFORM_PREVIEWS;
  const shouldShowPreview = previewFeatureEnabled && props.allowPreview;

  const refreshPreview = useCallback(
    (transforms: Array<LabAutomationTransform>, baseFileState: BaseFileState) => {
      if (transforms.every((transform) => isTransformValid(transform))) {
        const requestId = uuidv4();
        dispatchTransformPreviewState({
          type: TransformPreviewActionType.GENERATE_PREVIEW_STARTED,
          requestId,
        });
        generateTransformPreviews(
          baseFileState.unsignedUrl,
          baseFileState.name,
          props.automationFileConfig.name,
          props.baseSchema.id,
          transforms
        ).then(({previewFile}) => {
          dispatchTransformPreviewState({
            type: TransformPreviewActionType.GENERATE_PREVIEW_SUCCEEDED,
            requestId,
            previewFile,
          });
        });
      } else {
        dispatchTransformPreviewState({
          type: TransformPreviewActionType.GENERATE_PREVIEW_FAILED,
        });
      }
    },
    [props.automationFileConfig.name, props.baseSchema.id]
  );
  const debouncedTransforms = useDebounce(transforms, 300);
  useEffect(() => {
    if (shouldShowPreview && baseFileState?.unsignedUrl != null) {
      refreshPreview(debouncedTransforms, baseFileState);
    }
  }, [debouncedTransforms, baseFileState, shouldShowPreview, refreshPreview]);

  const setBaseFile = (file: File): void => {
    dispatchTransformPreviewState({
      type: TransformPreviewActionType.SET_BASE_FILE_STARTED,
    });
    ImportUtil.upload(file).then((unsignedUrl: string) => {
      generateBaseFilePreview(
        unsignedUrl,
        file.name,
        props.automationFileConfig.name,
        props.baseSchema.id
      ).then(({url}) => {
        dispatchTransformPreviewState({
          type: TransformPreviewActionType.SET_BASE_FILE_SUCCEEDED,
        });
        const newBaseFileState = {name: file.name, unsignedUrl, signedUrl: url};
        setBaseFileState(newBaseFileState);
      });
    });
  };

  const onRequestSubmit = (): void => {
    if (transforms.every((transform) => isTransformValid(transform))) {
      props.onRequestSubmit(transforms);
    } else {
      setSubmitAttempts(submitAttempts + 1);
    }
  };

  const modalProps = {
    fullWidth: shouldShowPreview,
    closeableOnBackgroundClick: false,
    closeableOnEscapeKey: false,
    onRequestSubmit,
    buttons: [
      {
        key: 'next',
        text: 'Done',
        style: ButtonStyle.SECONDARY,
        submit: true,
        disabled: transforms.length === 0,
      },
    ],
    onRequestClose: () => {
      analytics.trackEvent(
        RESULTS_ANALYTICS_ACTIONS.FINISHED_TRANSFORM_CONFIGURATION,
        props.analyticsMetadata
      );
      props.onRequestClose();
    },
    title: 'Set transforms',
    scrollable: true,
  };

  const isShowingBaseFilePreview =
    !transformPreviewState.isLoadingPreviews &&
    baseFileState?.signedUrl != null &&
    transformPreviewState.previewFile == null;

  const editor = (
    <ReorderableTransformsEditor
      baseSchema={props.baseSchema}
      transforms={transforms}
      handleUpdatedTransforms={setTransforms}
      submitAttempts={submitAttempts}
      allowReordering={previewFeatureEnabled}
    />
  );

  if (shouldShowPreview) {
    return (
      <Modal {...modalProps}>
        <div className={css(styles.wrapper)}>
          <div className={css(styles.leftPane)}>{editor}</div>
          <div className={css(styles.rightPane)}>
            <div className="form-group">
              <Row>
                <Col sm={12} className={css(styles.previewHeader)}>
                  <span className={css(styles.previewTitle)}>Preview</span>
                  {isShowingBaseFilePreview ? (
                    <div className={css(styles.baseFileBanner)}>
                      <BenchlingIcon icon="info-circle" />
                      <span>The following data has not been transformed yet.</span>
                    </div>
                  ) : null}
                  <UploadPreviewBaseFileButton setBaseFile={setBaseFile} />
                </Col>
              </Row>
              <Row>
                <Col sm={12}>
                  <TransformPreview
                    previewFile={transformPreviewState.previewFile}
                    isLoading={transformPreviewState.isLoadingPreviews}
                    setBaseFile={setBaseFile}
                    baseFileUrl={baseFileState?.signedUrl ?? null}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Modal>
    );
  }
  return <Modal {...modalProps}>{editor}</Modal>;
}

const styles = StyleSheet.create({
  leftPane: {
    display: 'flex',
    flexDirection: 'column',
    width: '40%',
    padding: '0 15px',
  },
  rightPane: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '60%',
    padding: '0 15px', // Offsets -15px margin of <Row> so we don't always need to horizontally scroll
    overflow: 'auto',
    maxHeight: '500px',
  },
  baseFileBanner: {
    backgroundColor: BLUE_POWDER,
    padding: '5px 10px',
    display: 'inline',
    marginRight: '8px',
    borderRadius: '3px',
  },
  previewHeader: {
    display: 'flex',
  },
  previewTitle: {
    ...FONT_PROXIMA_TITLE,
    marginRight: 'auto',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
  },
});
